import { AxiosPromise, AxiosResponse } from 'axios';
import { FieldState, FormState } from 'formstate';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';

import { FileDescriptor } from '@servicetitan/design-system';
import { DropdownFieldState, InputFieldState } from '@servicetitan/form';
import { NotificationsService } from '@servicetitan/notifications';
import { inject, injectable } from '@servicetitan/react-ioc';

import {
    ExecutionResult,
    PhonesPro,
    PlayGreetingModel,
} from '@servicetitan/application-data/telecom';

@injectable()
export class PlayGreetingStore {
    @observable isModalOpen = false;
    @observable savedGreeting?: { value: string; text?: string; isFile?: boolean };
    @observable defaultGreetings: Record<string, string> = {};
    @observable newCustomGreeting?: { value: string; text: string };

    formState = new FormState({
        playGreeting: new DropdownFieldState<string | undefined>(undefined),
        customGreetingFormState: new FormState({
            greetingType: new FieldState<string | undefined>(undefined),
            url: new InputFieldState<string>('').validators((value: string) => {
                if (this.formState.$.customGreetingFormState.$.greetingType.value !== 'Url') {
                    return;
                }
                if (value.length > 500) {
                    return 'The URL should be less than 500 symbols';
                }
                if (!value.endsWith('.mp3') && !value.endsWith('.wav')) {
                    return 'Please input a valid URL for .mp3 or .wav file';
                }
            }),
            file: new FieldState<FileDescriptor | undefined>(undefined),
            filePath: new FieldState<string | undefined>(undefined),
        }),
    });

    constructor(
        @inject(PhonesPro) private phonesProApi: PhonesPro,
        @inject(NotificationsService) private notifications: NotificationsService
    ) {
        makeObservable(this);
    }

    @action init = (model: PlayGreetingModel) => {
        this.defaultGreetings = model.defaultGreetings || {};
        this.savedGreeting = model.selectedGreeting
            ? {
                  value: model.selectedGreeting,
                  isFile: model.isFile,
              }
            : undefined;
        this.reset();
    };

    @computed get formHasChanges() {
        return this.formState.$.playGreeting.dirty;
    }

    @action reset = () => {
        this.formState.$.playGreeting.reset(
            this.savedGreeting ? this.savedGreeting.value : undefined
        );
        this.newCustomGreeting = undefined;
    };

    @action openModal = () => (this.isModalOpen = true);
    @action closeModal = () => {
        this.formState.$.customGreetingFormState.reset();
        this.isModalOpen = false;
    };

    @action uploadFile = async (file: File) => {
        try {
            const res = await this.phonesProApi.uploadGreetingFile(
                {
                    data: file,
                    fileName: file.name,
                },
                file.name
            );
            if (res?.data) {
                this.formState.$.customGreetingFormState.$.file.onChange({ file });
                this.formState.$.customGreetingFormState.$.filePath.onChange(res.data.result);
            }
        } catch {
            this.notifications.error({
                title: 'File Upload',
                message: 'An error occurred, please try again.',
            });
        }
    };

    @action handleFieldChange = <T extends AxiosResponse<ExecutionResult | null>>(
        field: FieldState<any>,
        response: T,
        action: (result: any) => void
    ) => {
        if (response?.data) {
            if (response.data.success) {
                runInAction(() => action(response!.data!.result));
            } else {
                field.setError(response.data.message || '');
                this.notifications.error({
                    title: 'Saving changes',
                    message: response.data.message ?? 'An error occurred, please try again.',
                });
            }
        }
    };

    @action save = async (
        isVoicemail?: boolean,
        saveAction?: (greeting: string) => AxiosPromise<ExecutionResult>
    ) => {
        // try {
        if ((await this.formState.validate()).hasError) {
            return;
        }

        const form = this.formState.$;

        if (
            (this.savedGreeting ? this.savedGreeting.value : undefined) !==
                form.playGreeting.value ||
            saveAction
        ) {
            const value = form.playGreeting.value;
            this.handleFieldChange(
                form.playGreeting,
                await (saveAction
                    ? saveAction(value || '')
                    : isVoicemail
                      ? this.phonesProApi.setVoicemailGreeting(value || '')
                      : this.phonesProApi.saveGreeting(value || '')),
                () => {
                    if (!value) {
                        this.newCustomGreeting = undefined;
                        this.savedGreeting = undefined;
                    } else if (this.defaultGreetings?.[value]) {
                        this.savedGreeting = { value };
                    } else {
                        this.savedGreeting = {
                            value: this.newCustomGreeting!.value!,
                            text: this.newCustomGreeting!.text!,
                            isFile: this.newCustomGreeting!.text !== this.newCustomGreeting!.value,
                        };
                    }
                    this.newCustomGreeting = undefined;
                }
            );
        }

        if (!this.formState.hasFieldError) {
            this.reset();
        }
    };

    uploadCustomGreeting = async () => {
        const form = this.formState.$.customGreetingFormState.$;
        const validationResult = await this.formState.$.customGreetingFormState.validate();
        if (validationResult.hasError) {
            return;
        }

        const text =
            form.greetingType.value === 'Url'
                ? form.url.value
                : (form.file.value!.file as File).name;
        const value = form.greetingType.value === 'Url' ? text : form.filePath.value + '*' + text;

        runInAction(() => {
            this.newCustomGreeting = { value, text };
            this.formState.$.playGreeting.onChange(this.newCustomGreeting.value);
        });
        this.closeModal();
    };

    @computed get savedGreetingName() {
        return (
            (this.savedGreeting &&
                (this.savedGreeting?.isFile
                    ? this.savedGreeting.text || this.savedGreeting.value
                    : (this.defaultGreetings[this.savedGreeting.value] ??
                      this.savedGreeting.value))) ??
            'No Greeting'
        );
    }
}
